import { FC, useState, useEffect } from 'react';
import { css } from '@emotion/react';
import Card from '../Card';
import ColoredPercentChange from '../ColoredPercentChange';
import { spacing, fontStyles, colors } from '../../styles';
import { collections as defaultCollectionImage } from '../../assets/collectionImages';

interface Props {
  name?: string;
  imageSrc?: string;
  isLocked?: boolean;
  returnValue?: number;
}

const CollectionCard: FC<Props> = ({ name, imageSrc, isLocked, returnValue }) => {
  const displayName = name?.trim() || 'Bloom Collection';
  const displayImageSrc = imageSrc || defaultCollectionImage;
  const [isDarkMode, setIsDarkMode] = useState(document.documentElement.getAttribute('data-theme') === 'dark');

  useEffect(() => {
    const checkTheme = () => {
      setIsDarkMode(document.documentElement.getAttribute('data-theme') === 'dark');
    };

    window.addEventListener('themeChanged', checkTheme);
    return () => window.removeEventListener('themeChanged', checkTheme);
  }, []);

  const shouldShowImageContainerBg = isDarkMode && !isLocked;
  const shouldShowLightBackground = isDarkMode && isLocked;

  return (
    <Card
      css={css`
        height: 100%;
        width: 100%;
        color: ${colors.petrolBlue};
        position: relative;
        padding: 12px 8px 8px 8px;
        ${fontStyles.fatCat};
        ${isLocked &&
        !isDarkMode &&
        `
        background-color: ${colors.cloud};
      `}
      `}
    >
      {isLocked && !isDarkMode ? (
        <div
          css={css`
            position: absolute;
            right: 4px;
            top: 4px;
            background: ${colors.petrolBlue};
            color: ${colors.sugar};
            padding: 2px ${spacing.milli};
            border-radius: 4px;
            ${fontStyles.flea}
            text-transform: uppercase;
            letter-spacing: 0.5px;
            z-index: 0;
          `}
        >
          Pro
        </div>
      ) : (
        returnValue !== undefined &&
        returnValue !== null && (
          <div
            css={css`
              position: absolute;
              right: 0;
              top: 0;
              padding: inherit;
              ${fontStyles.flea}
              z-index: 1;
            `}
          >
            <ColoredPercentChange value={returnValue} />
          </div>
        )
      )}
      <div
        css={css`
          display: flex;
          flex-direction: column;
          align-items: center;
          height: 100%;
          gap: ${spacing.milli};
        `}
      >
        <div
          css={css`
            width: 85%;
            position: relative;
            display: flex;
            align-items: center;
            justify-content: center;
            ${shouldShowImageContainerBg &&
            `
              background-color: #2a2a2a;
              border-radius: 12px;
              padding: 8px;
            `}
            ${shouldShowLightBackground &&
            `
              background-color: rgba(255, 255, 255, 0.25);
              border-radius: 12px;
              padding: 8px;
              backdrop-filter: blur(10px);
            `}
          `}
        >
          {isLocked && isDarkMode && (
            <div
              css={css`
                position: absolute;
                right: 0;
                top: 0;
                background: ${colors.petrolBlue};
                color: ${colors.sugar};
                padding: 2px ${spacing.milli};
                border-radius: 4px;
                ${fontStyles.flea}
                text-transform: uppercase;
                letter-spacing: 0.5px;
                z-index: 0;
              `}
            >
              Pro
            </div>
          )}
          <img
            src={displayImageSrc}
            alt={displayName}
            css={css`
              width: 100%;
              object-fit: contain;
            `}
          />
        </div>
        <div
          css={css`
            overflow: hidden;
            word-wrap: break-word;
            min-height: 2.4em;
            display: flex;
            align-items: center;
            justify-content: center;
            text-align: center;
            width: 100%;

            & > span {
              display: -webkit-box;
              -webkit-line-clamp: 2;
              -webkit-box-orient: vertical;
              overflow: hidden;
              width: 100%;
            }
          `}
        >
          <span>{displayName}</span>
        </div>
      </div>
    </Card>
  );
};

export default CollectionCard;
